import { useState, useEffect, useRef } from 'react';
import { Modal, Form, Select, Button, message, Spin } from 'antd';
import { RedoOutlined } from '@ant-design/icons';

const API = import.meta.env.VITE_API_BASE || '/api';
const token = () => localStorage.getItem('ar_token');
const h = () => ({ Authorization: 'Bearer ' + token() });

// 颜色选项：value = 爱签 color 参数 (2蓝/3黑)
const COLOR_OPTIONS = [
  { value: 2, label: '蓝色', hex: '#1E6FFF' },
  { value: 3, label: '黑色', hex: '#222222' },
];

// 字体选项：value = 爱签 fontFamily 参数
const FONT_OPTIONS = [
  { value: '宋体', label: '宋体（默认）' },
  { value: '楷体', label: '楷体' },
  { value: '华文行楷', label: '华文行楷' },
];

// 边框样式选项（已固定为横向）
const DEFAULT_BORDER_STYLE = 1;

// 预览颜色值（Canvas RGB 数组）
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return [r, g, b];
}

function fontToCanvas(family) {
  const map = { '宋体': 'SimSun', '楷体': 'KaiTi', '华文行楷': 'STXingkai' };
  return map[family] || 'SimSun';
}

/** 绘制预览到 canvas ref（模拟爱签样式）
 * border_style=1: 横向排列
 * border_style=2: 竖向排列（右起竖读，爱签会自动加"印"字）
 */
function drawPreview(canvas, signerName, colorIdx, fontFamily, hasBorder, borderStyle) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const W = 240, H = 160;
  canvas.width = W; canvas.height = H;

  ctx.clearRect(0, 0, W, H);

  const hex = COLOR_OPTIONS.find(c => c.value === colorIdx)?.hex || '#E53333';
  const fontFamily2 = fontToCanvas(fontFamily);

  // 竖向排列时，爱签会自动加"印"字
  const isVertical = false;  // 固定横向
  let displayText = signerName || '姓名';
  if (isVertical && displayText.length < 3) {
    displayText = displayText + '印';  // 模拟爱签行为
  }

  const fontSize = isVertical ? 48 : 40;
  ctx.font = `bold ${fontSize}px "${fontFamily2}", SimSun`;

  // 绘制边框
  if (hasBorder) {
    const pad = 8, lineGap = 3;
    ctx.strokeStyle = hex;
    ctx.lineWidth = 4;
    ctx.strokeRect(pad, pad, W - pad * 2, H - pad * 2);
    ctx.lineWidth = 2;
    ctx.strokeRect(pad + lineGap, pad + lineGap, W - (pad + lineGap) * 2, H - (pad + lineGap) * 2);
  }

  // 绘制文字
  ctx.fillStyle = hex;
  ctx.textBaseline = 'top';

  if (isVertical) {
    // 竖向排列：从右到左，每列一字
    const chars = displayText.split('');
    const charWidth = fontSize * 1.1;
    const charHeight = fontSize * 1.2;
    const totalWidth = chars.length * charWidth;
    const totalHeight = charHeight;
    const startX = (W - totalWidth) / 2 + charWidth * (chars.length - 1);
    const startY = (H - totalHeight) / 2;

    chars.forEach((char, idx) => {
      const x = startX - idx * charWidth;
      const y = startY;
      ctx.save();
      ctx.translate(x, y);
      ctx.fillText(char, 0, 0);
      ctx.restore();
    });
  } else {
    // 横向排列
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(displayText, W / 2, H / 2);
  }
}

export default function PersonalSealModal({ open, signer, onClose, onSuccess }) {
  const [form] = Form.useForm();
  const canvasRef = useRef(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [resultImg, setResultImg] = useState(null);

  useEffect(() => {
    if (open && signer) {
      form.setFieldsValue({
        seal_color: 2,
        seal_font: '宋体',
        has_border: 1,
      });
      setResultImg(null);
      // 立即用新 signer 重绘预览
      const name = signer?.signer_name || '';
      setTimeout(() => {
        const vals = form.getFieldsValue();
        drawPreview(canvasRef.current, name, vals.seal_color, vals.seal_font, vals.has_border, DEFAULT_BORDER_STYLE);
      }, 50);
    }
  }, [open, signer]);

  const watchValues = Form.useWatch([], form);

  useEffect(() => {
    if (!open) return;
    const vals = form.getFieldsValue();
    const name = signer?.signer_name || '';
    drawPreview(canvasRef.current, name, vals.seal_color, vals.seal_font, vals.has_border, DEFAULT_BORDER_STYLE);
  }, [open, signer, watchValues]);

  const handleValuesChange = () => {
    const vals = form.getFieldsValue();
    const name = signer?.signer_name || '';
    drawPreview(canvasRef.current, name, vals.seal_color, vals.seal_font, vals.has_border, DEFAULT_BORDER_STYLE);
  };

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields();
      setSubmitLoading(true);
      const signerName = signer?.signer_name || '';
      const payload = {
        seal_color: vals.seal_color,
        seal_font: vals.seal_font,
        has_border: vals.has_border ?? 1,
        border_style: DEFAULT_BORDER_STYLE,
        stamp_text: signerName,
        seal_title: signerName,
      };
      const r = await fetch(`${API}/customer/signer/${signer.id}/make-personal-seal`, {
        method: 'POST',
        headers: { ...h(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        let errMsg = `服务器错误 (${r.status})`;
        try { const errRes = await r.json(); errMsg = errRes.detail || errRes.message || errMsg; } catch {}
        message.error(errMsg);
        return;
      }
      const res = await r.json();
      if (res.success) {
        message.success('个人印章已生成（本地保存）');
        setResultImg(res.seal_img_url || null);
        onSuccess && onSuccess();
        setTimeout(() => {
          onClose();
          setResultImg(null);
        }, 2500);
      } else {
        message.error(res.message || '生成失败');
      }
    } catch (e) {
      if (e.errorFields) return;  // antd form validation
      message.error('网络请求失败，请检查网络连接后重试');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title="个人印章制作"
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
      destroyOnClose
    >
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
            为签收人 <strong>{signer?.signer_name}</strong> 制作个人印章
          </p>
          <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
            <Form.Item name="seal_color" label="印章颜色">
              <Select>
                {COLOR_OPTIONS.map(c => (
                  <Select.Option key={c.value} value={c.value}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: c.hex, border: '1px solid #ddd' }} />
                      {c.label}
                    </span>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="seal_font" label="字体">
              <Select>
                {FONT_OPTIONS.map(f => (
                  <Select.Option key={f.value} value={f.value}>{f.label}</Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item name="has_border" label="边框">
              <Select>
                <Select.Option value={1}>带边框</Select.Option>
                <Select.Option value={0}>无边框</Select.Option>
              </Select>
            </Form.Item>


            {resultImg ? (
              <div style={{ textAlign: 'center', color: '#52c41a', padding: '8px 0' }}>
                ✅ 印章已生成，弹窗即将关闭…
              </div>
            ) : (
              <Button
                type="primary"
                block
                loading={submitLoading}
                onClick={handleSubmit}
                icon={<RedoOutlined />}
              >
                生成印章
              </Button>
            )}
          </Form>
        </div>

        <div style={{ width: 260, flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8, textAlign: 'center' }}>预览</div>
          <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8, background: '#fafafa', textAlign: 'center' }}>
            {previewLoading ? (
              <Spin style={{ display: 'block', padding: '40px 0' }} />
            ) : (
              <canvas
                ref={canvasRef}
                style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }}
              />
            )}
          </div>
          <div style={{ fontSize: 11, color: '#bbb', marginTop: 6, textAlign: 'center' }}>
            * 预览供参考，实际以生成为准
          </div>
        </div>
      </div>
    </Modal>
  );
}
